import * as React from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

import { Scrollbar, ScrollbarWrapper, ScrolleableWrapper } from './horizontal-scroller.styled';

interface IScrollerProps {
  children: React.ReactNode;
  theme?: ScrollbarTheme;
  hidden?: boolean;
}

export enum ScrollbarTheme {
  DARK = 'dark',
  LIGHT = 'light',
}

export interface IUseScrollerRef {
  element: HTMLDivElement | null;
  scrollerWidthPercent: number;
}

export const HorizontalScroller = ({
  children,
  hidden = false,
  theme = ScrollbarTheme.DARK,
}: IScrollerProps) => {
  const [scrollLeft, setScrollLeft] = useState(0);
  const [showScrollbar, setShowScrollbar] = useState(false);

  const ref = useRef<IUseScrollerRef>({
    element: null,
    scrollerWidthPercent: 0,
  });

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    setScrollLeft(event.currentTarget.scrollLeft);
  };

  // Using setTimeout to win the race condition and do calculation after the component is completely done the render
  useLayoutEffect(() => {
    if (ref.current.element) {
      setTimeout(() => {
        const { element } = ref.current;
        const scrollWidth = element?.scrollWidth || 0;
        const offsetWidth = element?.offsetWidth || 0;
        const scrollerWidthPercent = (offsetWidth / scrollWidth) * 100;
        ref.current.scrollerWidthPercent = scrollerWidthPercent;
        setShowScrollbar(!hidden && scrollerWidthPercent < 100);
      }, 0);
    }
  }, [hidden]);

  return (
    <>
      <ScrolleableWrapper
        ref={e => {
          ref.current.element = e;
        }}
        onScroll={handleScroll}
      >
        {children}
      </ScrolleableWrapper>
      {showScrollbar && (
        <ScrollbarWrapper data-testid="scrollbar-wrapper" $theme={theme}>
          <Scrollbar
            widthPercent={ref.current.scrollerWidthPercent}
            scrollLeft={scrollLeft}
            scrollWidth={ref.current.element?.scrollWidth || 0}
          />
        </ScrollbarWrapper>
      )}
    </>
  );
};
